import React, {useCallback, useEffect, useMemo, useState} from "react";
import { GroutingData } from "../../../../types/GroutingData";
import { ColumnsType } from "antd/es/table";
import { Button, Input, message, Space, Table } from "antd";
import {
    addGroutingRow, deleteGroutingByGid,
    fetchGroutingByPid,
    updateGroutingRow
} from "../../../../backendInteraction/GroutingApiFetch";
import {numberValidator} from "../../../../utils/Validator";

interface GroutingTableProps {
    data: GroutingData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
    holeId: string;
    setHoleId: (value: string) => void;
}

const GroutingTable: React.FC<GroutingTableProps> = ( {data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow, holeId, setHoleId })=> {
    const [groutingData, setGroutingData] = useState<GroutingData[]>([]);
    const [editingGid, setEditingGid] = useState<number | null>(null);
    const [editData, setEditData] = useState<GroutingData | null>(null);

    useEffect(() => {
        setGroutingData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingGid !== -1 ) {
            const len = groutingData.length;
            const newRow: GroutingData = {
                key: -1,
                gid: -1,
                hole_id: len > 0 ? groutingData[len - 1 ].hole_id || holeId : holeId,
                drill_site: "",
                bags_used: "",
                metres: "",
                volume: "",
                pid: plodId ?? -1,
            }
            setGroutingData(prevData => {
                const hasNewRow = prevData.some(item => item.gid === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            })

            setEditingGid(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingGid]);


    useEffect(() => {
        if (isView) {
            if (editingGid === -1) {
                setGroutingData(prevData => prevData.filter(item => item.gid !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingGid(null);
            setEditData(null);
        }
    }, [isView])


    const saveChanges = useCallback( async () => {
        console.log("Saving changes:", editData);

        if(!editData) return;

        const { bags_used, metres, volume, hole_id } = editData;

        if(!!bags_used) {
            const isNumber = numberValidator(bags_used, 'Bags Used');
            if (!isNumber) return;
            if (parseFloat(bags_used) < 0) {
                message.error('The "Bags Used" value cannot be less than 0.');
                return;
            }
        }

        if(!!metres) {
            const isNumber = numberValidator(metres, 'Metres');
            if (!isNumber) return;
            if (parseFloat(metres) < 0) {
                message.error('The "Metres" value cannot be less than 0.');
                return;
            }
        }

        if(!!volume) {
            const isNumber = numberValidator(volume, 'Volume');
            if (!isNumber) return;
            if (parseFloat(volume) < 0) {
                message.error('The "Volume" value cannot be less than 0.');
                return;
            }
        }

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingGid === -1) {
            const success = await addGroutingRow(editData, aid);
            if (success) {
                setGroutingData( await fetchGroutingByPid(plodId ?? -1, aid));
                message.success("New grouting added successfully!");
            } else {
                message.error("Failed to add new grouting.");
            }
        } else {
            const success = await updateGroutingRow(editingGid ?? -1, editData);
            if (success) {
                setGroutingData( await fetchGroutingByPid(plodId ?? -1, aid));
                message.success("Grouting updated successfully!");
            } else {
                message.error("Failed to update grouting.");
            }
        }

        !(hole_id === holeId) && setHoleId(hole_id);

        if (editingGid === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingGid(null);
        setEditData(null);
    }, [editData, editingGid,setIsAddNewRow]);

    const cancelEditing = useCallback((record: GroutingData) => {
        console.log("Cancel editing");
        setEditingGid(null);
        setEditData(null);

        if (record.gid === -1) {
            setGroutingData(prevData => prevData.filter(item => item.gid !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: GroutingData) => {
        console.log("Editing row :", record);
        if ( editingGid === -1 ) {
            setGroutingData(prevData => prevData.filter(item => item.gid !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingGid( prevId => {
            if (prevId !== null) cancelEditing({gid: prevId} as GroutingData);
            return record.gid;
        });

        setEditData({ ...record }) ;
    }, [editingGid, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: GroutingData) => {
            console.log("Deleting row:", record);
    
            const storedUser = localStorage.getItem("DORS_USER");
            const aid = storedUser && JSON.parse(storedUser).Aid;
    
            const success = await deleteGroutingByGid(record.gid);
            if (success) {
                setGroutingData(await fetchGroutingByPid(plodId ?? -1, aid));
                message.success("Grouting deleted successfully!");
            } else {
                message.error("Failed to delete grouting.");
            }
    }, []);

    const handleInputChange = useCallback((key: keyof GroutingData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);


    const columns: ColumnsType<GroutingData> = useMemo(() => [
        {
            title: "Hole ID",
            dataIndex: "hole_id",
            key: "hole_id",
            width: "16%",
            render: (_, record) =>
                editingGid === record.gid ? (
                    <Input
                        value={editData?.hole_id}
                        onChange={(e) => handleInputChange("hole_id", e.target.value)}
                    />
                ) : (
                    record.hole_id
               ),
        },
        {
            title: 'Drill Site',
            dataIndex: 'drill_site',
            key: 'drill_site',
            width: "16%",
            render: (_, record) =>
                editingGid === record.gid ? (
                    <Input
                        value={editData?.drill_site}
                        onChange={(e) => handleInputChange("drill_site", e.target.value)}
                    />
                ) : (
                    record.drill_site
                ),
        },
        {
            title: 'Bags Used',
            dataIndex: 'bags_used',
            key: 'bags_used',
            width: "16%",
            render: (_, record) =>
                editingGid === record.gid ? (
                    <Input
                        value={editData?.bags_used}
                        onChange={(e) => handleInputChange("bags_used", e.target.value)}
                    />
                ) : (
                    record.bags_used
                ),
        },
        {
            title: 'Metres',
            dataIndex: 'metres',
            key: 'metres',
            width: "16%",
            render: (_, record) =>
                editingGid === record.gid ? (
                    <Input
                        value={editData?.metres}
                        onChange={(e) => handleInputChange("metres", e.target.value)}
                    />
                ) : (
                    record.metres
                ),
        },
        {
            title: 'Volume',
            dataIndex: 'volume',
            key: 'volume',
            width: "16%",
            render: (_, record) =>
                editingGid === record.gid ? (
                    <Input
                        value={editData?.volume}
                        onChange={(e) => handleInputChange("volume", e.target.value)}
                    />
                ) : (
                    record.volume
                ),
        },
        ...(!isView
                ? [{
                    title: 'Action',
                    key: 'action',
                    width: "20%",
                    render: (_: any , record: GroutingData) => (
                        <Space size="small">
                            <Space size="small">
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingGid === record.gid ? saveChanges() : startEditing(record))}>
                                    {editingGid === record.gid ? "Save" : "Edit"}
                                </Button>
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingGid === record.gid ? cancelEditing(record) : deleteRow(record))}>
                                    {editingGid === record.gid ? "Cancel" : "Delete"}
                                </Button>
                            </Space>
                        </Space>
                    ),
                }] as ColumnsType<GroutingData>
                : []
        ),
    ], [isView, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<GroutingData>
            columns={columns}
            pagination={false}
            dataSource={groutingData}
        />
    );
};

export default GroutingTable;